import React, { useState } from "react";
import toast from "react-hot-toast";
import { apiService } from "../services/api";
import LoadingSpinner from "../components/common/LoadingSpinner";
import { ArrowDownTrayIcon, UserGroupIcon } from "@heroicons/react/24/outline";

interface Result {
  email: string;
  companyName?: string;
  status: string;
  dealerId: string;
  sponsoredCustomerCount: number;
  hasTransactions: string;
  transactionCount: number;
  storeCount: number;
  stores: Array<{
    storeId: string;
    storeName: string;
    status: string;
  }>;
}

const BulkDealerCheck: React.FC = () => {
  const [emails, setEmails] = useState("");
  const [results, setResults] = useState<Result[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDealerIds, setSelectedDealerIds] = useState<string[]>([]);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleCheck = async () => {
    const emailList = emails
      .split(/[\n,;]/)
      .map((e) => e.trim())
      .filter((e) => e.length > 0 && e.includes("@"));

    if (emailList.length === 0) {
      toast.error("Please enter valid emails");
      return;
    }

    setIsLoading(true);
    setSelectedDealerIds([]);
    try {
      const response = await apiService.bulkCheckDealerStats(emailList);
      if (response.success) {
        setResults(response.data);
        toast.success(`Checked ${response.data.length} emails`);
      } else {
        toast.error("Failed to check stats");
      }
    } catch (error: any) {
      toast.error(error.message || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleSelectAll = () => {
    if (selectedDealerIds.length === results.filter(r => r.status === 'FOUND').length) {
      setSelectedDealerIds([]);
    } else {
      setSelectedDealerIds(results.filter(r => r.status === 'FOUND').map(r => r.dealerId));
    }
  };

  const handleToggleSelectDealer = (dealerId: string) => {
    setSelectedDealerIds(prev => 
      prev.includes(dealerId) 
        ? prev.filter(id => id !== dealerId) 
        : [...prev, dealerId]
    );
  };

  const handleBulkUpdateStores = async (status: "active" | "pending" | "inactive") => {
    if (selectedDealerIds.length === 0) {
      toast.error("Please select at least one dealer");
      return;
    }

    if (!window.confirm(`Are you sure you want to set ALL stores of ${selectedDealerIds.length} dealers to ${status}?`)) {
      return;
    }

    setIsUpdating(true);
    try {
      const response = await apiService.bulkUpdateStoresStatus(selectedDealerIds, status);
      if (response.success) {
        toast.success(`Successfully updated ${response.updatedDealers} dealers`);
        // Refresh results to show new store statuses
        handleCheck();
      } else {
        toast.error("Failed to update stores");
      }
    } catch (error: any) {
      toast.error(error.message || "An error occurred during update");
    } finally {
      setIsUpdating(false);
    }
  };

  const exportToCsv = (data: Result[], filename: string) => {
    if (data.length === 0) return;
 
    const headers = ["Email", "Company Name", "Status", "Dealer ID", "Sponsored Count", "Has Transactions", "Transaction Count", "Store Count", "Store IDs"];
    const rows = data.map((r) => [
      r.email,
      r.companyName || "-",
      r.status,
      `="${r.dealerId}"`, // Quote to keep as string in Excel
      r.sponsoredCustomerCount,
      r.hasTransactions,
      r.transactionCount,
      r.storeCount || 0,
      r.stores ? `="${r.stores.map(s => s.storeId).join("; ")}"` : "-",
    ]);
 
    const content = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${filename}-${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportAll = () => exportToCsv(results, "dealer-stats-all");
  
  const handleExportInactive = () => {
    const inactive = results.filter(r => r.sponsoredCustomerCount === 0 && r.transactionCount === 0);
    if (inactive.length === 0) {
      toast.error("No inactive dealers found in results");
      return;
    }
    exportToCsv(inactive, "dealer-stats-inactive");
  };

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <UserGroupIcon className="h-8 w-8 text-blue-500" />
          Bulk Dealer Stats Checker
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Paste a list of emails (comma or newline separated) to check dealer statistics.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 border border-gray-200 dark:border-gray-700">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email Addresses
            </label>
            <textarea
              className="w-full h-64 p-3 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none resize-none dark:text-white font-mono text-sm"
              placeholder="dealer1@example.com&#10;dealer2@example.com"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
            />
            <button
              onClick={handleCheck}
              disabled={isLoading || !emails.trim()}
              className="w-full mt-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition-colors flex items-center justify-center gap-2"
            >
              {isLoading ? <LoadingSpinner size="sm" /> : "Check Stats"}
            </button>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 min-h-[400px]">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Results {results.length > 0 && `(${results.length})`}
                </h2>
                {selectedDealerIds.length > 0 && (
                  <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 px-3 py-1 rounded-full border border-blue-100 dark:border-blue-800/50">
                    <span className="text-xs font-medium text-blue-700 dark:text-blue-400">
                      {selectedDealerIds.length} selected
                    </span>
                    <button
                      onClick={() => handleBulkUpdateStores("pending")}
                      disabled={isUpdating}
                      className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2 py-0.5 rounded transition-colors disabled:bg-gray-400"
                    >
                      Set Pending
                    </button>
                    <button
                      onClick={() => handleBulkUpdateStores("inactive")}
                      disabled={isUpdating}
                      className="text-xs bg-red-600 hover:bg-red-700 text-white px-2 py-0.5 rounded transition-colors disabled:bg-gray-400"
                    >
                      Set Inactive
                    </button>
                  </div>
                )}
              </div>
              {results.length > 0 && (
                <div className="flex gap-2">
                  <button
                    onClick={handleExportInactive}
                    className="flex items-center gap-1 text-sm bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/20 dark:hover:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800/50 px-3 py-1.5 rounded-lg transition-colors"
                    title="Emails with 0 sponsors and 0 transactions"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    Export Inactive
                  </button>
                  <button
                    onClick={handleExportAll}
                    className="flex items-center gap-1 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-3 py-1.5 rounded-lg transition-colors"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    Export All
                  </button>
                </div>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50 dark:bg-gray-900/50 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        checked={results.length > 0 && results.filter(r => r.status === 'FOUND').length > 0 && selectedDealerIds.length === results.filter(r => r.status === 'FOUND').length}
                        onChange={handleToggleSelectAll}
                      />
                    </th>
                    <th className="px-4 py-3">Email / Company</th>
                    <th className="px-4 py-3 text-center">Stores</th>
                    <th className="px-4 py-3 text-center">Sponsored</th>
                    <th className="px-4 py-3 text-center">Transactions</th>
                    <th className="px-4 py-3">Dealer ID</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {results.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                        No results to display. Enter emails and click "Check Stats".
                      </td>
                    </tr>
                  ) : (
                    results.map((result, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="px-4 py-3">
                          {result.status === 'FOUND' && (
                            <input
                              type="checkbox"
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              checked={selectedDealerIds.includes(result.dealerId)}
                              onChange={() => handleToggleSelectDealer(result.dealerId)}
                            />
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full ${
                              result.status === 'FOUND' ? 'bg-green-500' : 'bg-red-500'
                            }`} />
                            <div className="font-medium text-gray-900 dark:text-white">{result.email}</div>
                          </div>
                          {result.companyName && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 ml-4">{result.companyName}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex flex-col items-center">
                            <span className="text-sm font-bold text-gray-900 dark:text-white">
                              {result.storeCount || 0}
                            </span>
                            {result.stores && result.stores.length > 0 && (
                              <div className="flex flex-wrap gap-1 justify-center mt-1">
                                {result.stores.map((s, sIdx) => (
                                  <span 
                                    key={sIdx} 
                                    className={`text-[8px] px-1 rounded-sm border ${
                                      s.status === 'active' 
                                        ? 'bg-green-50 text-green-700 border-green-200' 
                                        : s.status === 'pending'
                                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                                          : 'bg-red-50 text-red-700 border-red-200'
                                    }`}
                                    title={`${s.storeName} (${s.storeId})`}
                                  >
                                    {s.status.substring(0, 1).toUpperCase()}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-center font-semibold text-gray-900 dark:text-white">
                          {result.sponsoredCustomerCount}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex flex-col items-center">
                            <span className="text-sm font-bold text-gray-900 dark:text-white">
                              {result.transactionCount}
                            </span>
                            <span className={`text-[10px] uppercase font-medium ${
                              result.hasTransactions === 'YES' ? 'text-blue-600' : 'text-gray-400'
                            }`}>
                              {result.hasTransactions}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs font-mono text-gray-500 dark:text-gray-400">
                          {result.dealerId}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkDealerCheck;
